import React from "react";
import { Alert, Button, Checkbox, Modal, Space, Tag, Typography } from "antd";
import { ArrowRightOutlined, SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatSyncValue, notSelectedProperties } from "functions/productData";
import { SHOP_COLORS, shopLabel } from "./constants";

const { Text } = Typography;

/**
 * "Je hebt dit hier gewijzigd - dit staat er bij de andere shop. Ook daar toepassen?"
 *
 * Deliberately not a yes/no question about "het product": it names the other product and lists
 * every value it would overwrite, old next to new, with a tick per line. The whole point of the
 * link is that one person's edit lands on a second piece of data in a second Firebase project;
 * that is not something to confirm blind, and unticking one line has to be possible - the
 * content is the same in both shops, the note for the picker sometimes is not.
 *
 * It never appears when nothing would change: the plan is built first, and a plan without
 * changes is not a question.
 *
 * `canApply` is false when this session may not write to the target shop (no HVO session). The
 * prompt is still shown then - that a counterpart exists, and what stands there, is worth
 * seeing - but the button that would write is disabled and says why, rather than offering a
 * write the data layer is certain to refuse. The rest of the screen locks HVO the same way.
 */
const SyncPromptModal = ({
  open,
  plan,
  selectedKeys,
  source,
  target,
  applying,
  canApply = true,
  onChange,
  onApply,
  onSkip,
  onEditSyncedProperties,
}) => {
  const { t } = useTranslation();

  const changes = (plan && plan.changes) || [];
  const skipped = notSelectedProperties(plan);

  return (
    <Modal
      open={open}
      width={640}
      onCancel={onSkip}
      maskClosable={false}
      title={t("product_data.sync_title", { shop: shopLabel(target && target.shopSource) })}
      footer={[
        <Button key="skip" onClick={onSkip} disabled={applying}>
          {t("product_data.sync_skip")}
        </Button>,
        <Button
          key="apply"
          type="primary"
          loading={applying}
          disabled={!canApply || !selectedKeys || selectedKeys.length === 0}
          onClick={onApply}
        >
          {t("product_data.sync_apply")}
        </Button>,
      ]}
    >
      <p>
        {t("product_data.sync_intro", {
          product: (source && source.name) || "-",
          shop: shopLabel(source && source.shopSource),
        })}
      </p>

      <div
        style={{
          border: "1px solid #e6e6e6",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
        }}
      >
        <Space align="center" className="mb-2">
          <Tag color={SHOP_COLORS[(target && target.shopSource) || "HVO"]}>
            {(target && target.shopSource) || "-"}
          </Tag>
          <strong>{(target && target.name) || t("product_data.unknown_product")}</strong>
        </Space>

        <Checkbox.Group value={selectedKeys} onChange={onChange} style={{ width: "100%" }}>
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {changes.map((change) => (
              <Checkbox key={change.key} value={change.key}>
                <span>
                  <strong>{change.label}</strong>
                  {": "}
                  <Text type="secondary" delete>
                    {formatSyncValue(change.key, change.from)}
                  </Text>{" "}
                  <ArrowRightOutlined style={{ fontSize: 11 }} />{" "}
                  <strong>{formatSyncValue(change.key, change.to)}</strong>
                </span>
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </div>

      {!canApply && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("product_data.no_hvo_session_title")}
          description={t("product_data.no_hvo_session_sync", {
            shop: shopLabel(target && target.shopSource),
          })}
        />
      )}

      {skipped.length > 0 && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("product_data.sync_not_selected", {
            properties: skipped.map((entry) => entry.label).join(", "),
          })}
        />
      )}

      <Button type="link" size="small" icon={<SettingOutlined />} onClick={onEditSyncedProperties} style={{ paddingLeft: 0 }}>
        {t("product_data.synced_properties_button")}
      </Button>
    </Modal>
  );
};

export default SyncPromptModal;
